import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

export interface CreateFhirApolloClientOptions {
  /** Base URL of the FHIR server, e.g. https://medplum.eluve.dev */
  baseUrl: string;
  /**
   * Returns the current Bearer token for the FHIR server. Called on every
   * request so token refreshes upstream propagate without needing to rebuild
   * the client.
   */
  getAccessToken: () => Promise<string | undefined> | string | undefined;
  /**
   * Awaited before each request — lets the provider re-exchange the token
   * if it is expired or about to expire. Inline check avoids relying on a
   * setTimeout (unreliable in backgrounded tabs and across sleep/wake).
   */
  ensureFreshToken?: () => Promise<void>;
  /**
   * Called when the FHIR server returns 401 — signals the provider to
   * re-run full init. Last-resort fallback for cases where the inline
   * freshness check passes but the server still rejects (revocation,
   * key rotation, clock skew).
   */
  onUnauthorized?: () => void;
}

/**
 * Apollo client for the tenant-scoped FHIR GraphQL endpoint. Separate from
 * the Hasura Apollo client so its cache, auth, and network log entries stay
 * distinct. Name is "fhir" so it shows up labelled that way in Apollo
 * DevTools.
 */
export const createFhirApolloClient = (
  options: CreateFhirApolloClientOptions,
): ApolloClient<NormalizedCacheObject> => {
  const { baseUrl, getAccessToken, ensureFreshToken, onUnauthorized } = options;

  const authLink = setContext(async (_req, { headers }) => {
    if (ensureFreshToken) {
      try {
        await ensureFreshToken();
      } catch {
        // If re-exchange fails, fall through with the stale token. The
        // request will 401 and onUnauthorized will trigger full re-init.
      }
    }
    const token = await getAccessToken();
    return {
      headers: {
        ...(headers as Record<string, string>),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Medplum': 'extended',
      },
    };
  });

  const errorLink = onError(({ networkError }) => {
    if (
      onUnauthorized &&
      networkError &&
      'statusCode' in networkError &&
      networkError.statusCode === 401
    ) {
      onUnauthorized();
    }
  });

  const httpLink = new HttpLink({
    uri: `${baseUrl.replace(/\/$/, '')}/fhir/R4/$graphql`,
    fetch,
  });

  return new ApolloClient({
    name: 'fhir',
    cache: new InMemoryCache({
      typePolicies: {
        Patient: { keyFields: ['id'] },
        Practitioner: { keyFields: ['id'] },
      },
    }),
    link: from([errorLink, authLink, httpLink]),
    defaultOptions: {
      watchQuery: { fetchPolicy: 'cache-and-network', errorPolicy: 'all' },
      query: { fetchPolicy: 'network-only', errorPolicy: 'all' },
    },
    devtools: { enabled: process.env.NODE_ENV === 'development' },
  });
};
